// Перенесены ли долги, бывшие до начала работы в программе, — и как сказать об этом словами.
//
// ## Зачем
//
// Пять мест продукта отвечали на пустой список долгов «долгов нет»: вкладка человека, реестр
// долгов, сводка расчёта, плитка пульта и свод в карточке подотчётного. 23.09.2026 заказчик
// второй раз написал, что у человека долги точно есть. Он был прав: остатки на старт не
// переносились вовсе, и пустота значила «сюда ещё ничего не переносили», а не «никто не должен».
//
// Здесь признак с сервера и ОДНИ слова на все пять мест. Пять экранов, каждый со своей
// формулировкой одного и того же факта, — это пять разных фактов для человека, который их
// читает (правило «одно сообщение, а не два разными словами»).
//
// ## Три состояния, а не два
//
// Признак может ещё не приехать или не приехать вовсе. Тогда ни «перенесены», ни «не
// перенесены» сказать нельзя, и пустота называется без оговорки про перенос — ровно тем, что
// известно наверняка: в программе долгов нет.

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { OpeningBalancesOut } from '@/shared/api/endpoints/losses';
import { fetchOpeningBalances } from '@/shared/api/endpoints/losses';
import type { ApiError } from '@/shared/errors/api-error';

export type СостояниеПереноса = 'перенесены' | 'не перенесены' | 'неизвестно';

/**
 * Был ли перенос остатков долгов. Один ответ на всю программу.
 *
 * `включено` — там, где долги человеку не открыты: спрашивать признак, который он всё равно
 * не увидит, значит получить отказ сервера на каждом открытии экрана.
 */
export function useПереносДолгов(включено = true): UseQueryResult<OpeningBalancesOut, ApiError> {
  return useQuery<OpeningBalancesOut, ApiError>({
    queryKey: ['debts', 'opening-balances'],
    queryFn: fetchOpeningBalances,
    enabled: включено,
    // Перенос делается один раз за всю жизнь программы: спрашивать чаще раза в пять минут
    // незачем, а каждый экран с долгами иначе дёргал бы сервер заново.
    staleTime: 5 * 60_000,
  });
}

export function состояниеПереноса(
  перенос: UseQueryResult<OpeningBalancesOut, ApiError>,
): СостояниеПереноса {
  if (перенос.data === undefined) {
    return 'неизвестно';
  }
  return перенос.data.migrated ? 'перенесены' : 'не перенесены';
}

/**
 * Панель переноса остатков листом: живёт в «Расчёт» → «Долги», а открывают её и из карточки
 * человека. Опознаватели объявлены ЗДЕСЬ, в общем слое, а раздел «Расчёт» их только
 * пересказывает: иначе карточке человека пришлось бы ходить за ними в чужой раздел.
 */
export const PANEL_DEBTS_TRANSFER = 'debts-transfer';
export const DEBTS_TRANSFER_ID = 'sheet';
/** Адрес переноса из любого места программы. */
export const АДРЕС_ПЕРЕНОСА = `/payroll/debts?p=${PANEL_DEBTS_TRANSFER}:${DEBTS_TRANSFER_ID}`;

/** Как найти перенос словами: одна фраза на все места, где его упоминают. */
const КАК_ПЕРЕНЕСТИ =
  'Перенести их можно сразу у всех — одним листом из Excel: «Расчёт» → «Долги» → «Перенести остатки».';

/** Та же фраза там, где переход к переносу стоит прямо под ней: пересказывать путь незачем. */
export const КАК_ПЕРЕНЕСТИ_ЗДЕСЬ = 'Перенести их можно сразу у всех — одним листом из Excel.';

/** Одна и та же оговорка во всех местах, где пустота иначе читалась бы как «никто не должен». */
export const ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ =
  'Долги, которые были до начала работы в программе, — за форму, билеты, обучение, — сюда ещё не перенесены.';

/** Откуда возьмутся новые долги. Без этого пустота не отвечает на вопрос «а что дальше». */
const ПОЯВЯТСЯ_САМИ = 'Новые появятся сами: при выдаче формы и приёмке чека с признаком долга.';

/** Пустой список долгов ОДНОГО человека словами. */
export function пустоУЧеловека(состояние: СостояниеПереноса): {
  title: string;
  description: string;
} {
  if (состояние === 'перенесены') {
    return {
      title: 'Долгов нет',
      description:
        'За человеком не числится ни формы, ни билета, ни обучения. Долг появится здесь, как только его заведут или выдадут форму.',
    };
  }
  if (состояние === 'не перенесены') {
    return {
      title: 'В программе долгов по нему нет',
      // Путь не пересказывается: переход к переносу стоит в карточке прямо под этой фразой.
      description: `${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ} ${КАК_ПЕРЕНЕСТИ_ЗДЕСЬ} Один старый долг — кнопкой «Завести долг» здесь.`,
    };
  }
  return {
    title: 'В программе долгов по нему нет',
    description: 'Долг появится здесь, как только его заведут или выдадут форму.',
  };
}

/**
 * Пустой список долгов ВСЕХ — реестр и сводка расчёта.
 *
 * `кнопкаРядом` — экран сам несёт кнопку переноса (реестр долгов): тогда путь «Расчёт» →
 * «Долги» не пересказывается, человек уже здесь.
 */
export function пустоУВсех(
  состояние: СостояниеПереноса,
  { кнопкаРядом = false }: { кнопкаРядом?: boolean } = {},
): {
  title: string;
  description: string;
} {
  if (состояние === 'перенесены') {
    return {
      title: 'Долгов нет ни за кем',
      description:
        'Долг появится здесь, как только его заведут в карточке человека или выдадут форму.',
    };
  }
  if (состояние === 'не перенесены') {
    return {
      title: 'В программе долгов пока нет',
      description: `${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ.replace('сюда ещё', 'ещё')} ${кнопкаРядом ? КАК_ПЕРЕНЕСТИ_ЗДЕСЬ : КАК_ПЕРЕНЕСТИ} ${ПОЯВЯТСЯ_САМИ}`,
    };
  }
  return {
    title: 'В программе долгов пока нет',
    description:
      'Долг появится здесь, как только его заведут в карточке человека или выдадут форму.',
  };
}
